import { DateTime, Namespace, TempusDominus } from "@eonasdan/tempus-dominus";
import { dom } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";
import { PropTypes } from "prop-types";
import React, { useEffect, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "~/lib/dayjs";

const getIcon = (pickerType) =>
  pickerType === "time" ? "far fa-clock" : "far fa-calendar-alt";

// Custom icons using "far" (regular) prefix - matches icons registered in fontawesome.js
const pickerIcons = {
  time: "far fa-clock",
  date: "far fa-calendar",
  up: "far fa-arrow-up",
  down: "far fa-arrow-down",
  previous: "far fa-chevron-left",
  next: "far fa-chevron-right",
  today: "far fa-calendar-check",
  clear: "far fa-trash",
  close: "far fa-times",
};
const isSameDate = (date1, date2, format) => {
  if (!date1 || !date2) {
    return false;
  }

  const date1Formatted = date1.format(format);
  const date2Formatted = date2.format(format);

  return date1Formatted === date2Formatted;
};

const DateTimePicker = ({
  id = "",
  format = "LL",
  value = "",
  placeholder = "",
  pickerType = "datetime",
  bulk = false,
  showTodayButton = false,
  showClear = false,
  useCurrent = false,
  isInvalid = false,
  isDisabled = false,
  preserveInvalidInput = false,
  minDate = undefined,
  maxDate = undefined,
  onChange = () => {
    // noop
  },
  children = null,
}) => {
  const pickerElement = useRef(null);
  const controlRef = useRef(null);
  const faIcon = useMemo(() => getIcon(pickerType), [pickerType]);

  useEffect(() => {
    if (pickerElement.current) {
      controlRef.current = new TempusDominus(pickerElement.current, {
        useCurrent,
        localization: {
          locale: "en-US",
        },
        restrictions: {
          minDate,
          maxDate,
        },
        display: {
          theme: "light",
          buttons: {
            clear: showClear,
            today: showTodayButton,
          },
          components: {
            decades: false,
            year: pickerType !== "time",
            month: pickerType !== "time",
            date: pickerType !== "time",
            hours: pickerType !== "date",
            minutes: pickerType !== "date",
            seconds: pickerType === "datetime",
          },
          icons: pickerIcons,
        },
      });

      controlRef.current.dates.formatInput = (date) =>
        date ? dayjs(date).format(format) : "";

      controlRef.current.subscribe(Namespace.events.change, (e) => {
        onChange(e.date ? dayjs(e.date) : null);
      });

      // Convert FontAwesome CSS classes to SVGs when picker is shown
      // The picker widget is rendered outside the component, so target the widget container
      controlRef.current.subscribe(Namespace.events.show, () => {
        const widget = controlRef.current?.display?.widget;
        if (widget) {
          dom.i2svg({ node: widget });
        }
      });
    }

    return () => controlRef.current?.dispose();
  }, [pickerElement.current]);

  useEffect(() => {
    const dateValue = dayjs(value, format, true);
    const newDate = value && dateValue.isValid() ? dateValue : null;

    if (controlRef.current && newDate) {
      const current = controlRef.current.dates.lastPicked
        ? dayjs(controlRef.current.dates.lastPicked)
        : null;

      if (!current || !isSameDate(newDate, current, format)) {
        controlRef.current.dates.setFromInput(
          DateTime.convert(newDate.toDate()),
        );

        const input = pickerElement.current.querySelector("input");
        if (input) {
          input.value = newDate.format(format);
        }
      }
    } else if (controlRef.current && !newDate) {
      // Controlled custom inputs may retain partial/invalid text for validation.
      if (preserveInvalidInput && value) {
        return;
      }
      // Clear the picker when value is reset (e.g., operator change)
      controlRef.current.dates.clear();
      const input = pickerElement.current?.querySelector("input");
      if (input) {
        input.value = "";
      }
    }
  }, [value, format, preserveInvalidInput]);

  const renderTrigger = () => {
    if (children) {
      return children;
    }

    return (
      <div className="input-group">
        <input
          id={id || uuidv4()}
          disabled={isDisabled}
          type="text"
          className={classnames("form-control", { "has-error": isInvalid })}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              controlRef.current?.toggle();
            }
          }}
          onChange={(e) => {
            if (!e.target.value) {
              onChange(null);
              return;
            }
            const parsed = dayjs(e.target.value, format, true);
            if (parsed.isValid()) {
              onChange(parsed);
            }
          }}
        />
        <div className="input-group-append">
          <span className="input-group-text">
            <FontAwesomeIcon icon={faIcon} />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className={bulk ? "" : "position-relative col p0"} ref={pickerElement}>
      {renderTrigger()}
    </div>
  );
};

DateTimePicker.propTypes = {
  id: PropTypes.string,
  format: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  placeholder: PropTypes.string,
  pickerType: PropTypes.string,
  bulk: PropTypes.bool,
  showTodayButton: PropTypes.bool,
  showClear: PropTypes.bool,
  useCurrent: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isDisabled: PropTypes.bool,
  preserveInvalidInput: PropTypes.bool,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  children: PropTypes.node,
};

export default DateTimePicker;
